/**
 * SupervisorRecordsSection — سجلات المشرفين والموظفين (مختصرة).
 * كل صف: الاسم + البريد + الهاتف + زر "عرض" (يفتح Dialog فيه كل التفاصيل والمرفقات).
 */
import { useState } from 'react';
import {
  FileText, Search, RefreshCw, Loader2, User, Mail, Phone, Archive, Eye, Paperclip, UserCheck,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import SupervisorDetailDialog from './SupervisorDetailDialog';

function RecordRow({ rec, language, onView, onArchive }) {
  const attCount = Array.isArray(rec.attachments) ? rec.attachments.length : 0;
  return (
    <div
      className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a4e] hover:border-amber-500/30 transition-colors flex items-center justify-between gap-4 flex-wrap"
      data-testid={`supervisor-record-${rec.id}`}
    >
      {/* Left: avatar + name */}
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
          <User className="h-5 w-5 text-amber-400" />
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-white truncate">
            {rec.full_name_ar
              || `${rec.first_name || ''} ${rec.last_name || ''}`.trim()
              || (language === 'ar' ? 'بدون اسم' : 'No name')}
          </p>
          {rec.employee_id && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#d4af37]/15 text-[#d4af37] mt-0.5 inline-block">
              ID: {rec.employee_id}
            </span>
          )}
          {rec.linked_user_id && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 mt-0.5 ms-1 inline-flex items-center gap-1"
              title={language === 'ar' ? 'لهذا الموظف حساب يوزر فعّال' : 'This employee has an active user account'}
              data-testid={`linked-user-badge-${rec.id}`}
            >
              <UserCheck className="h-3 w-3" />
              {language === 'ar' ? 'يوزر مرتبط' : 'User linked'}
            </span>
          )}
        </div>
      </div>

      {/* Middle: email + phone in a clean line */}
      <div className="flex flex-col md:flex-row md:items-center gap-x-4 gap-y-1 text-sm text-[#A1A1AA] min-w-0 flex-1">
        {rec.email && (
          <span className="flex items-center gap-1 truncate" dir="ltr">
            <Mail className="h-3.5 w-3.5 text-[#71717A] shrink-0" />
            <span className="truncate">{rec.email}</span>
          </span>
        )}
        {rec.phone && (
          <span className="flex items-center gap-1 truncate" dir="ltr">
            <Phone className="h-3.5 w-3.5 text-[#71717A] shrink-0" />
            <span>{rec.phone}</span>
          </span>
        )}
      </div>

      {/* Right: actions */}
      <div className="flex items-center gap-2 shrink-0">
        {attCount > 0 && (
          <span className="text-[10px] px-2 py-1 rounded-full bg-blue-500/10 text-blue-300 flex items-center gap-1" data-testid={`attachment-count-${rec.id}`}>
            <Paperclip className="h-3 w-3" />
            {attCount}
          </span>
        )}
        <Button
          size="sm"
          onClick={() => onView(rec)}
          className="h-8 bg-amber-600 hover:bg-amber-700 text-white"
          data-testid={`view-supervisor-${rec.id}`}
        >
          <Eye className="h-3.5 w-3.5 me-1" />
          {language === 'ar' ? 'عرض' : 'View'}
        </Button>
        <Button
          size="sm" variant="outline"
          onClick={() => onArchive?.(rec)}
          className="h-8 px-2 border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
          title={language === 'ar' ? 'أرشفة' : 'Archive'}
          data-testid={`archive-supervisor-${rec.id}`}
        >
          <Archive className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function filterRecords(records, query) {
  if (!query.trim()) return records;
  const q = query.toLowerCase();
  return records.filter((r) =>
    [r.full_name_ar, r.full_name_en, r.first_name, r.last_name, r.email,
      r.phone, r.id_number, r.employee_id, r.job_title, r.job_title_ar]
      .some((v) => v && String(v).toLowerCase().includes(q)),
  );
}

export default function SupervisorRecordsSection({
  language,
  supervisorRecords,
  loadingSupervisorRecords,
  supervisorRecordsSearch,
  setSupervisorRecordsSearch,
  fetchSupervisorRecords,
  onArchive,
}) {
  const [viewId, setViewId] = useState(null);
  const visible = filterRecords(supervisorRecords, supervisorRecordsSearch);

  return (
    <div
      className="bg-[#1a1a2e] rounded-3xl border border-[#2a2a4e] p-6"
      data-testid="supervisor-records-section"
    >
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <FileText className="h-5 w-5 text-amber-400" />
          {language === 'ar' ? 'سجلات المشرفين والموظفين' : 'Supervisors & Staff Records'}
          <span className="text-sm font-normal text-[#71717A]">({supervisorRecords.length})</span>
        </h2>
        <div className="flex items-center gap-2 flex-1 sm:flex-none min-w-[260px] max-w-md">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]" />
            <input
              type="text"
              value={supervisorRecordsSearch}
              onChange={(e) => setSupervisorRecordsSearch(e.target.value)}
              placeholder={language === 'ar' ? 'بحث بالاسم/البريد/الهوية...' : 'Search by name/email/ID...'}
              className="w-full ps-3 pe-9 py-2 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl text-white text-sm focus:border-amber-500/50 focus:outline-none"
              data-testid="supervisor-records-search"
            />
          </div>
          <Button
            onClick={fetchSupervisorRecords}
            variant="outline" size="sm"
            className="border-white/10 text-white hover:bg-white/5"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loadingSupervisorRecords ? (
        <div className="text-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-amber-400 mx-auto" />
          <p className="text-[#808080] mt-2">{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</p>
        </div>
      ) : supervisorRecords.length === 0 ? (
        <div className="text-center py-16 text-[#808080]">
          <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p className="text-base">{language === 'ar' ? 'لا توجد سجلات حالياً' : 'No records yet'}</p>
          <p className="text-sm text-[#606060] mt-1">
            {language === 'ar'
              ? 'استخدم زر "إضافة المشرفين والموظفين" لإدراج سجل جديد'
              : 'Use "Add Supervisors & Employees" to register a new record'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((rec) => (
            <RecordRow
              key={rec.id}
              rec={rec}
              language={language}
              onView={() => setViewId(rec.id)}
              onArchive={onArchive}
            />
          ))}
        </div>
      )}

      <SupervisorDetailDialog
        open={!!viewId}
        onOpenChange={(o) => !o && setViewId(null)}
        supervisorId={viewId}
        language={language}
        onChanged={fetchSupervisorRecords}
      />
    </div>
  );
}
